import { ChevronDown, ChevronUp, Copy, Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { copyTextToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";
import { useProjectNavigate } from "../routing/project-route-context";

interface AssistantPlanCardProps {
  content: string;
  className?: string;
}

const planTypographyClasses = cn(
  "text-foreground",
  "[&_h1]:text-lg [&_h1]:font-bold [&_h1]:leading-snug [&_h1]:mb-4",
  "[&_h2]:text-base [&_h2]:font-bold [&_h2]:leading-snug [&_h2]:mb-3",
  "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:leading-normal [&_h3]:mb-2",
  "[&_p]:text-[13px] [&_p]:leading-[1.7] [&_p]:mb-3 [&_p:last-child]:mb-0",
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol_li]:my-1.5",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:my-1",
  "[&_li]:text-[13px] [&_li]:leading-[1.7]",
  "[&_strong]:font-semibold"
);

export function AssistantPlanCard({ content, className }: AssistantPlanCardProps) {
  const navigate = useProjectNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCopy = () =>
    copyTextToClipboard(content, {
      success: "Plan copied to clipboard",
      failure: "Failed to copy plan",
    });

  return (
    <div className={cn("w-full border border-border bg-muted", className)}>
      {/* Header bar */}
      <div className="flex items-center gap-2 px-5 py-3">
        <Sparkles className="size-3.5 text-primary" />
        <span className="text-xs font-semibold text-primary">Plan</span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => void handleCopy()}
          aria-label="Copy plan"
        >
          <Copy className="size-3.5 text-neutral-dim" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={isExpanded ? "Collapse plan" : "Expand plan"}
        >
          {isExpanded ? (
            <ChevronUp className="size-3.5 text-neutral-dim" />
          ) : (
            <ChevronDown className="size-3.5 text-neutral-dim" />
          )}
        </Button>
      </div>

      {/* Body */}
      {isExpanded ? (
        <div className="px-5 pb-5">
          <MessageMarkdown
            content={content}
            onNavigate={navigate}
            className={planTypographyClasses}
          />
        </div>
      ) : (
        <div className="relative">
          <div className="max-h-[260px] overflow-hidden px-5 pb-5">
            <MessageMarkdown
              content={content}
              onNavigate={navigate}
              className={planTypographyClasses}
            />
          </div>
          {/* Gradient overlay */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-muted to-transparent" />
          {/* Expand button */}
          <div className="flex justify-center pb-3">
            <button
              type="button"
              className={cn(
                "flex items-center gap-1.5 border border-border bg-background px-4 py-1.5",
                "text-xs font-medium text-muted-foreground transition-colors",
                "no-touch:hover:bg-muted"
              )}
              onClick={() => setIsExpanded(true)}
            >
              Expand plan
              <ChevronDown className="size-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
